import { useMemo } from 'react'
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'
import { useDebounce } from 'use-debounce'
import _ from 'lodash'
import * as uuid from 'uuid'

import { useLineItemStore } from 'admin/pages/RecordEdit/LineItemStore'

import { useApi } from './api'
import {
  formatDeliveryProductsForSave,
  formatDeliveryRawProductsForSave,
  formatWasteProductsForSave,
  formatWasteRawProductsForSave
} from './dataFormatters'
import { getCancellationToken } from './core'
import { Guid } from 'admin/lib/guid'
import { getCancellable, queryKeys, queryify } from './queries-common'

/**
 * @param {{ scope: 'user'|'global' }} param0
 */
export const useAccounts = ({ scope = 'user' } = {}) => {
  const api = useApi()

  return useQuery([queryKeys.listAccountsAndProjects, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    if (scope === 'user') {
      return queryify(api.listAccounts()).then(accounts =>
        _.sortBy(accounts, account => account.name?.toLowerCase())
      )
    }

    if (scope === 'global') {
      return queryify(api.adminListAccounts()).then(accounts =>
        _.sortBy(accounts, account => account.name?.toLowerCase())
      )
    }

    throw `Scope: ${scope} is not supported`
  })
}

export const useAccount = accountId => {
  const api = useApi()

  return useQuery([queryKeys.listAccountsAndProjects, accountId, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.getAccount(accountId))
  })
}

export const useProject = (accountId, projectId) => {
  const api = useApi()

  return useQuery(
    [queryKeys.listAccountsAndProjects, accountId, projectId, !!api],
    () => {
      if (!api) {
        return getCancellable()
      }

      return queryify(api.getProject(accountId, projectId))
    }
  )
}

export const useReferenceProductItems = (searchTerm = null) => {
  const api = useApi()

  const [debouncedSearchTerm] = useDebounce(searchTerm, 300, {
    trailing: true,
    leading: false
  })

  return useInfiniteQuery(
    [queryKeys.getReferenceProductItems, !!api, debouncedSearchTerm],
    context => {
      if (!api) {
        return getCancellable()
      }

      const maybeContinuationToken = context.pageParam
      if (maybeContinuationToken === false) {
        return
      }

      const cancellationToken = getCancellationToken()

      return queryify(
        api.getReferenceProductItems({
          pageSize: 100,
          continuationToken: maybeContinuationToken,
          searchTerm: debouncedSearchTerm,
          cancelToken: cancellationToken.token
        }),
        cancellationToken.cancel
      )
    },
    {
      getNextPageParam: page => {
        return page.hasNextPage && page.continuationToken
      },
      getPreviousPageParam: () => false,
      keepPreviousData: true
    }
  )
}

export const useReferenceProductItem = (id = '') => {
  const api = useApi()

  return useQuery([queryKeys.getReferenceProductItems, id, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    if (id === Guid.Empty) {
      return Promise.resolve(null)
    }

    return queryify(api.getReferenceProductItem(id))
  })
}

export const useReferenceProductItemsByIds = (ids = []) => {
  const api = useApi()

  return useQuery([queryKeys.getReferenceProductItems, ids, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    const filteredIds = ids.filter(id => id !== Guid.Empty)

    return Promise.all(
      filteredIds.map(id => {
        return queryify(api.getReferenceProductItem(id))
      })
    )
  })
}

export const useCertificates = () => {
  const api = useApi()

  return useQuery([queryKeys.getCertificates, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.getCertificates())
  })
}

export const useTemplateNames = () => {
  const api = useApi()

  return useQuery([queryKeys.getTemplateNames, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.getTemplateNames())
  })
}

export const useCompanies = () => {
  const api = useApi()

  return useQuery([queryKeys.getCompanies, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.getCompanies())
  })
}

export const useCompany = companyId => {
  const api = useApi()

  return useQuery([queryKeys.getCompany(companyId), !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.getCompany(companyId))
  })
}

export const useCompanyByIds = (ids = []) => {
  const api = useApi()

  return useQuery([queryKeys.getCompanies, ids, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    const filteredIds = ids.filter(id => id !== Guid.Empty)

    return Promise.all(
      filteredIds.map(id => {
        return queryify(api.getCompany(id))
      })
    )
  })
}

export const useCompanyMerge = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    async ({ targetCompanyId, otherCompanyIds = [] } = {}) => {
      return queryify(api.mergeCompanies(targetCompanyId, otherCompanyIds))
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.getCompanies)
      },
      retry: 3,
      retryDelay: attempt => 1000 * attempt
    }
  )
}

export const useCompanyDelete = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    async ({ companyId }) => {
      return queryify(api.deleteCompany(companyId))
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.getCompanies)
      },
      retry: 3,
      retryDelay: attempt => 1000 * attempt
    }
  )
}

export const useCompanyUpsert = (isNew = false) => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    ({ company }) => {
      if (isNew) {
        return queryify(api.registerCompany(company))
      } else {
        return queryify(api.updateCompany(company))
      }
    },
    {
      onSuccess: (response, { company }) => {
        queryClient.invalidateQueries(queryKeys.getCompanies)
        queryClient.invalidateQueries(queryKeys.getCompany(company.companyId))
      }
    }
  )
}

export const useUser = userId => {
  const api = useApi()

  return useQuery([queryKeys.getUser(userId), !!api], () => {
    if (!api) {
      return getCancellable()
    }

    if (!userId) {
      return Promise.resolve(null)
    }

    return queryify(api.getUser(userId)).catch(function (error) {
      if (error.response.status > 400) {
        return Promise.resolve(null)
      }
    })
  })
}

export const useUserAccountMappings = userId => {
  const api = useApi()

  return useQuery([queryKeys.getUserAccountMappings(userId), !!api], () => {
    if (!api) {
      return getCancellable()
    }

    if (!userId) {
      return Promise.resolve(null)
    }

    return queryify(api.getUserAccountMappings(userId)).catch(function (error) {
      if (error.response.status > 400) {
        return Promise.resolve(null)
      }
    })
  })
}

export const useSSOIDPs = () => {
  const api = useApi()

  return useQuery([queryKeys.listSSOIDPs, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.listSSOIDPs()).catch(function (error) {
      if (error.response.status > 400) {
        return Promise.resolve(null)
      }
    })
  })
}

export const useUserSelf = () => {
  const api = useApi()

  return useQuery([queryKeys.userSelf, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.getUserSelf())
  })
}

export const useUserSelfAccountMappings = () => {
  const api = useApi()

  return useQuery([queryKeys.userSelfAccountMappings, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.getUserSelfAccountMappings())
  })
}

export const useUserUpsert = (isNew = false) => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    ({ user }) => {
      if (isNew) {
        return queryify(api.registerUser(user))
      } else {
        return queryify(api.updateUser(user))
      }
    },
    {
      onSuccess: (response, { user }) => {
        queryClient.invalidateQueries(queryKeys.listUsers)
        queryClient.invalidateQueries(queryKeys.listUserAccountMappings)
        if (user.userId) {
          queryClient.invalidateQueries(queryKeys.getUser(user.userId))
          queryClient.invalidateQueries(
            queryKeys.getUserAccountMappings(user.userId)
          )
          queryClient.invalidateQueries(queryKeys.userSelf)
          queryClient.invalidateQueries(queryKeys.userSelfAccountMappings)
        }
      }
    }
  )
}

export const useUserResetPassword = () => {
  const api = useApi()

  return useMutation(({ userId }) => {
    return queryify(api.resetUserPassword(userId))
  })
}

export const useProductGroups = ({ accountId, projectId }) => {
  const api = useApi()

  return useQuery(
    [queryKeys.getProductGroups, !!api, { accountId, projectId }],
    () => {
      if (!api) {
        return getCancellable()
      }

      return queryify(api.getProductGroups(accountId, projectId))
    }
  )
}

export const useSmartWasteConfig = ({ accountId, projectId }) => {
  const api = useApi()

  return useQuery(
    [queryKeys.getSmartWasteConfig(projectId), !!api, { accountId, projectId }],
    () => {
      if (!api) {
        return getCancellable()
      }

      return queryify(api.getSmartWasteConfig(accountId, projectId))
    }
  )
}

export const useApiKeys = () => {
  const api = useApi()

  return useQuery([queryKeys.getApiKeys, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.listApiKeys())
  })
}

export const useApiKey = id => {
  const api = useApi()

  return useQuery([queryKeys.getApiKeys, !!api, id], () => {
    if (!api) {
      return getCancellable()
    }

    if (!id) {
      return Promise.resolve(null)
    }

    return queryify(api.getApiKey(id))
  })
}

export const useApiKeyRegister = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    [queryKeys.getApiKeys, !!api],
    ({ data }) => {
      if (!api) {
        return getCancellable()
      }

      return queryify(api.registerApiKey(data))
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.getApiKeys)
      }
    }
  )
}

export const useApiKeyUpdater = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    [queryKeys.getApiKeys, !!api],
    ({ data }) => {
      if (!api) {
        return getCancellable()
      }

      return queryify(api.updateApiKey(data.hashedKey, data))
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.getApiKeys)
      }
    }
  )
}

export const useRecord = (accountId, projectId, moduleName, recordId) => {
  const api = useApi()

  return useQuery(
    [
      queryKeys.record(recordId),
      !!api,
      accountId,
      projectId,
      moduleName,
      recordId
    ],
    () => {
      if (!api) {
        return getCancellable()
      }

      return queryify(api.getRecord(accountId, projectId, moduleName, recordId))
    },
    {
      // This is used on forms, and refetching could cause a form reset,
      // so ensure refetch is only possible explicitly through cache invalidation or page reload
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      staleTime: Infinity
    }
  )
}

export const useRecordUpdater = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    [queryKeys.records, !!api],
    ({
      accountId,
      projectId,
      moduleName,
      recordId,
      record,
      isImperial,
      timeZone,
      dataView
    }) => {
      const isDelivery = moduleName.toUpperCase() === 'DELIVERY'

      const mergedRecord = { ...record }
      // Get item values from state
      const tempItems = _.cloneDeep(useLineItemStore.getState())

      // remove the 'deleteKey' and 'duplicateKey' keys from this object
      delete tempItems['deleteKey']
      delete tempItems['duplicateKey']

      // Convert nested objects to array of line item objects and remove hookFormId properties
      // And if no ID is set generate unique Id
      const items = Object.values(tempItems)
        .filter(item => typeof item === 'object')
        .map(item => {
          delete item['hookFormId']
          const syncedIds = uuid.v4()
          if (item.id && !item.raw_id) {
            item.raw_id = item.id
          }
          if (!item.id) {
            item.id = syncedIds
          }
          if (!item.raw_id) {
            item.raw_id = syncedIds
          }
          return item
        })

      if (isDelivery) {
        const enrichedItems = formatDeliveryProductsForSave(
          items,
          timeZone,
          isImperial
        )

        // Keeping the following for backwards support (i.e pre-Raw & Enriched)
        mergedRecord.data = {
          ...record.data,
          products: enrichedItems
        }

        if (record.enriched) {
          mergedRecord.enriched = {
            ...record.enriched,
            products: enrichedItems
          }
        }

        const rawItems = formatDeliveryRawProductsForSave(items)
        mergedRecord.raw = {
          ...record.raw,
          products: rawItems
        }
      } else {
        // If Waste
        const enrichedItems = formatWasteProductsForSave(items)

        // Keeping the following for backwards support (i.e pre-Raw & Enriched)
        mergedRecord.data = {
          ...record.data,
          items: enrichedItems
        }

        if (record.enriched) {
          mergedRecord.enriched = {
            ...record.enriched,
            items: enrichedItems
          }
        }

        const rawItems = formatWasteRawProductsForSave(items)

        mergedRecord.raw = {
          ...record.raw,
          items: rawItems
        }
      }

      if (!api) {
        return getCancellable()
      }

      return queryify(
        api.updateRecord(
          accountId,
          projectId,
          moduleName,
          recordId,
          mergedRecord,
          dataView
        )
      )
    },
    {
      onSuccess: (data, { recordId }) => {
        queryClient.invalidateQueries(queryKeys.records)
        queryClient.invalidateQueries(queryKeys.record(recordId))
      }
    }
  )
}

export const useRecordModuleTransferer = () => {
  const api = useApi()

  return useMutation(
    [queryKeys.transferRecordToModule, !!api],
    ({ accountId, projectId, moduleName, recordId, destinationModuleName }) => {
      if (!api) {
        return getCancellable()
      }

      return queryify(
        api.transferRecordToModule(
          accountId,
          projectId,
          moduleName,
          recordId,
          destinationModuleName
        )
      )
    },
    {
      onSuccess: () => {
        // reset records once they're loaded by query
      }
    }
  )
}

export const useRecordProjectTransferer = () => {
  const api = useApi()

  return useMutation(
    [queryKeys.transferRecordToProject, !!api],
    ({ accountId, projectId, moduleName, recordId, destinationProjectId }) => {
      if (!api) {
        return getCancellable()
      }

      return queryify(
        api.transferRecordToProject(
          accountId,
          projectId,
          moduleName,
          recordId,
          destinationProjectId
        )
      )
    },
    {
      onSuccess: () => {
        // This does nothing but the success token is used to trigger the load of the new record
      }
    }
  )
}

export const useAutoComplete = ({
  projectId,
  moduleName,
  fieldName,
  alias
}) => {
  const api = useApi()

  const { mutateAsync, data } = useMutation(
    ['autocomplete', !!api, projectId, moduleName, fieldName, alias],
    hint => {
      if (!api) {
        return getCancellable()
      }

      if (!hint) {
        return Promise.resolve({ completions: [] })
      }

      const cancellationToken = getCancellationToken()

      return queryify(
        api.autocomplete(
          {
            projectId,
            moduleName,
            fieldName,
            alias,
            hint
          },
          cancellationToken.token
        ),
        cancellationToken.cancel
      )
    },
    {}
  )

  const update = useMemo(
    () => _.debounce(mutateAsync, 400, { leading: false, trailing: true }),
    [mutateAsync]
  )

  return [data?.completions ?? [], update]
}

export const useUsers = () => {
  const api = useApi()

  return useQuery([queryKeys.listUsers, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.listUsers()).then(users =>
      _.sortBy(users, users => users.displayName.toLowerCase())
    )
  })
}

export const useUsersAccountMappings = () => {
  const api = useApi()

  return useQuery([queryKeys.listUserAccountMappings, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.listUserAccountMappings())
  })
}

export const useQflowUsers = () => {
  const api = useApi()

  return useQuery([queryKeys.listQflowUsers, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.listQflowUsers()).then(users => {
      const filteredUsers = users.filter(user => user.accountEnabled)
      return _.sortBy(filteredUsers, user => user.displayName.toLowerCase())
    })
  })
}

export const useBIReports = () => {
  const api = useApi()

  return useQuery([queryKeys.listBIReports, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.listBIReports())
  })
}

export const useBIReportsUserPermissions = accountId => {
  const api = useApi()

  return useQuery(
    [queryKeys.biReportsUserPermissions(accountId), !!api],
    () => {
      if (!api) {
        return getCancellable()
      }

      return queryify(api.biReportsUserPermissions(accountId))
    }
  )
}

export const useBIReportsWorkspaces = () => {
  const api = useApi()

  return useQuery([queryKeys.listBIReportsWorkspaces, !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.biReportsWorkspaces())
  })
}

export const useBIReportsWorkspaceReports = workspaceId => {
  const api = useApi()

  return useQuery(
    [queryKeys.biReportsWorkspaceReports(workspaceId), !!api],
    () => {
      if (
        workspaceId === '' ||
        workspaceId == null ||
        workspaceId === Guid.Empty
      ) {
        return null
      }
      if (!api) {
        return getCancellable()
      }

      return queryify(api.biReportsWorkspaceReports(workspaceId))
    }
  )
}

export const useAccountBIReportUpsert = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    ({ accountId, biReports }) => {
      return queryify(api.upsertAccountBIReports(accountId, biReports))
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.listBIReports)
      }
    }
  )
}

export const useAccountBIReportUserPermissionsUpsert = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    ({ accountId, userPermissions }) => {
      return queryify(
        api.upsertAccountBIReportsUserPermissions(accountId, userPermissions)
      )
    },
    {
      onSuccess: (data, { accountId }) => {
        queryClient.invalidateQueries(
          queryKeys.biReportsUserPermissions(accountId)
        )
      }
    }
  )
}
